// Route sweeps (docs/ART-DIRECTION.md §7 City captured, taxgw-2ybg): ownership colour revealed along a physical setup
// edge's static route geometry, so every mandatory corner survives. Each routeTrail effect reserves its own scaled
// slot range, and each reserved slot belongs to a known effect at CONSTRUCTION: its def, mesh, material, uniforms,
// start scalar, always-valid live colour and selection are one record. SELECTION is the only active-state authority —
// the shared immutable unset value (idle) or a ReadyRoute (active) — so no nullable-field combination can express an
// illegal state. play() assigns an existing ready route/colour/time with no allocation; update() narrows the
// selection before drawing, and expiry resets it to the shared unset value. Materials take the reveal [tail, head],
// the shared alpha envelope and the dash/scroll style; the fragment clips outside the interval on the full fixed strip.

import { BufferGeometry, Color, Group, Mesh, type IUniform, type ShaderMaterial } from 'three'
import { InvariantViolation } from '@taxwar/net'
import { EMISSIVE_LAYER, assignLayer } from '../render/layers'
import { effectsOf, type EffectId, type RouteTrailDef } from './effects'
import { additiveMaterial } from './gpu'
import { UNSET_ROUTE_SELECTION, type RoutePlacement, type RouteSelection } from './placement'
import { ROUTE_TRAIL_FRAGMENT, ROUTE_TRAIL_VERTEX } from './shaders'
import { SlotRange, scaledCount } from './slots'
import { envelopeAt, trailHead, trailTail } from './timing'
import type { VfxUniforms } from './uniforms'

/** A route sweep whose visible span is shorter than this path fraction is not drawn this frame. */
const MIN_SPAN = 1e-3

/** Per-slot uniform objects, kept so update() writes them directly (no map lookups, no allocation). */
interface RouteSlotUniforms {
  readonly tint: IUniform<Color>
  readonly alpha: IUniform<number>
  readonly tailAlpha: IUniform<number>
  readonly head: IUniform<number>
  readonly tail: IUniform<number>
  readonly halfWidth: IUniform<number>
  readonly dashesPerUnit: IUniform<number>
  readonly dashDuty: IUniform<number>
  readonly scroll: IUniform<number>
  readonly core: IUniform<number>
  readonly reverse: IUniform<number>
  readonly routeLength: IUniform<number>
}

/**
 * One preallocated route slot. Its effect and GPU objects are fixed at construction (the slot was reserved by the
 * effect whose scaled range contains it); `start` and `colour` are written on play, never allocated. `selection` is
 * the ONLY field that says whether the slot is active, so `def`/`mesh`/`material`/`uniforms` can never disagree with
 * one another.
 */
interface RouteSlot {
  readonly def: RouteTrailDef
  readonly mesh: Mesh
  readonly material: ShaderMaterial
  readonly uniforms: RouteSlotUniforms
  /** Wall-clock time the reveal began (already includes def.delay); meaningful only while `selection` is a route. */
  start: number
  /** The live palette colour of the active route; held by reference and valid at all times (unchanged while idle). */
  colour: Color
  /** The only active-state authority: the shared unset value while idle, or the ready route being drawn. */
  selection: RouteSelection
}

export class RouteTrailSystem {
  /** Added to the war VFX layer's root; every slot mesh lives here. */
  readonly root = new Group()

  private readonly ranges = new Map<EffectId, SlotRange>()
  /** Every slot, in effect/range order; a slot's index is the value SlotRange.claim() returns. */
  private readonly slots: readonly RouteSlot[]
  /** One shared placeholder geometry; slots swap to their route's geometry on play. */
  private readonly placeholder = new BufferGeometry()

  /** Reserves the existing scaled maxConcurrent slots per routeTrail effect and preallocates one mesh + material each. */
  constructor(uniforms: VfxUniforms, hotCore: Color, scale: number) {
    const hotCoreUniform: IUniform<Color> = { value: hotCore }
    const slots: RouteSlot[] = []
    for (const [id, def] of effectsOf('routeTrail')) {
      const count = scaledCount(def.maxConcurrent, scale)
      this.ranges.set(id, new SlotRange(slots.length, count))
      for (let i = 0; i < count; i++) {
        const slot: RouteSlotUniforms = {
          tint: { value: new Color() },
          alpha: { value: 0 },
          tailAlpha: { value: 1 },
          head: { value: 0 },
          tail: { value: 0 },
          halfWidth: { value: 0 },
          dashesPerUnit: { value: 0 },
          dashDuty: { value: 1 },
          scroll: { value: 0 },
          core: { value: 0 },
          reverse: { value: 0 },
          routeLength: { value: 0 },
        }
        const material = additiveMaterial(ROUTE_TRAIL_VERTEX, ROUTE_TRAIL_FRAGMENT, {
          uTime: uniforms.uTime,
          uHotCore: hotCoreUniform,
          uTint: slot.tint,
          uAlpha: slot.alpha,
          uTailAlpha: slot.tailAlpha,
          uHead: slot.head,
          uTail: slot.tail,
          uHalfWidth: slot.halfWidth,
          uDashesPerUnit: slot.dashesPerUnit,
          uDashDuty: slot.dashDuty,
          uScroll: slot.scroll,
          uCore: slot.core,
          uReverse: slot.reverse,
          uRouteLength: slot.routeLength,
        })
        const mesh = new Mesh(this.placeholder, material)
        mesh.visible = false
        mesh.frustumCulled = false
        assignLayer(mesh, EMISSIVE_LAYER)
        this.root.add(mesh)
        slots.push({
          def,
          mesh,
          material,
          uniforms: slot,
          start: 0,
          colour: new Color(),
          selection: UNSET_ROUTE_SELECTION,
        })
      }
    }
    this.slots = slots
  }

  /** `colour` is a live palette colour: kept by reference, never mutated. `placement` must carry a ready route. */
  play(id: EffectId, placement: RoutePlacement, colour: Color, now: number): void {
    const range = this.ranges.get(id)
    if (range === undefined) throw new InvariantViolation(`routeTrail effect ${id} has no reserved slots`)
    const selection = placement.selection
    if (selection.kind !== 'route') throw new InvariantViolation(`routeTrail effect ${id} was played with an unset route selection; the binding must call setRoute first`)
    const slot = this.slots[range.claim()] as RouteSlot
    slot.mesh.geometry = selection.geometry.bufferGeometry
    slot.mesh.visible = false
    slot.start = now + slot.def.delay
    slot.colour = colour
    slot.selection = selection
  }

  update(now: number): void {
    for (const slot of this.slots) {
      const selection = slot.selection
      if (selection.kind === 'unset') continue
      const age = now - slot.start
      if (age < 0) continue
      const def = slot.def
      const t = age / def.duration
      if (t >= 1) {
        slot.mesh.visible = false
        slot.selection = UNSET_ROUTE_SELECTION
        continue
      }
      const head = trailHead(def.head, t)
      const tail = trailTail(head, def.tail)
      const u = slot.uniforms
      u.tint.value.copy(slot.colour).multiplyScalar(def.intensity)
      u.alpha.value = envelopeAt(def.envelope, t)
      u.tailAlpha.value = def.tailAlpha
      u.head.value = head
      u.tail.value = tail
      u.halfWidth.value = def.style.width / 2
      u.dashesPerUnit.value = def.style.dashesPerUnit
      u.dashDuty.value = def.style.dashDuty
      u.scroll.value = def.style.scroll
      u.core.value = def.style.core
      u.reverse.value = selection.direction === 1 ? 0 : 1
      u.routeLength.value = selection.geometry.lengthWorld
      slot.mesh.visible = head - tail >= MIN_SPAN
    }
  }

  /** Releases this system's materials and slot meshes; the shared route geometries belong to the cache. */
  dispose(): void {
    this.root.removeFromParent()
    this.root.clear()
    for (const slot of this.slots) slot.material.dispose()
    this.placeholder.dispose()
  }
}
